import { lazy, useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';
import useUserAccount from '../hooks/userAccountHook';
import AuthLayout from '../layouts/AuthLayout';
import DashboardLayout from '../layouts/DashboardLayout';
import ErrorsLayout from '../layouts/ErrorsLayout';
import { DASHBOARD, DASHBOARD_BASE, FORGOT_PASSWORD, NOT_FOUND, RESET_PASSWORD, SIGN_IN, COMPLETE_ACCOUNT } from '../resources/links';
import CreateLeads from '../views/admin/leads/CreateLead';
import ForgotPassword from '../views/auth/ForgotPassword';
import ResetPassword from '../views/auth/ResetPassword';
import Auth from '../views/auth/index';
import NotFound from '../views/errors/NotFound';
import LeadDetailsView from '../views/admin/leads/LeadDetailsView';
import ViewCustomer from '../views/shared/customers/ViewCustomer';
import RoleDetailsView from '../views/shared/settings/RolesAndPermisions/RoleDetails';
import CreateRoleView from '../views/shared/settings/RolesAndPermisions/CreateCustomRole';
import CompleteAccount from '../views/auth/CompleteAccount';
import Vehicles from '../views/admin/vehicles';
import AddInsurer from '../views/partners/insurers/AddInsurer';
import useRoute, { useNavigationStack } from '../hooks/routesHook';
import RelationshipManagerTeam from '../views/partners/RelationshipManagerTeam';
import InsurerOnboarding from '../components/dashboard/insurers/onboarding/InsurerOnboarding';
import useAuth from '../hooks/authHook';
import InsurerStorePreview from '../components/dashboard/insurers/store/InsurerStorePreview';
import RNPLApplicantDetails from '../views/shared/rnpl/RNPLApplicantDetails';
import { isProduction } from '../utils/miscFunctions';
import RouteWrapper from './RouteWrapper'
import PublicRouteWrapper from './PublicRouteWrapper';
import PremiumFinancingApplicationDetails from '../views/shared/premiumFinancing/PremiumFinancingApplicationDetails';
import VerificationDetails from '../views/shared/vehicle-verification/VerificationDetails';
import PremiumFinancingEdit from '../views/shared/premiumFinancing/PremiumFinancingEdit';
import AdminCreatePremiumFinancingApplication from '../views/shared/premiumFinancing/CreatePremiumFinancingApplication';
import TransactionDetailPage from '../views/partners/financier/transactions/TransactionDetailPage';
import CompanyDetail from '../views/partners/CompanyDetails';
import BillDetailsView from '../views/partners/shared/bills/BillDetailsView';
import { useDispatch } from 'react-redux';
import { getAllEnums } from '../api/requests';
import { IEnumsResponse } from '../resources/interfaces';
import { UPDATE_ENUMS } from '../states/slices/resourceSlice';
import { useQuery } from '@tanstack/react-query';
import CreateSubscriptionPlan from '../views/shared/settings/billing/CreateSubscriptionPlan';
import DashboardView from '../views/shared/dashboard';
import PoliciesView from '../views/shared/policies';
import CustomersView from '../views/shared/customers';
import LeadsView from '../views/admin/leads';
import QuotesView from '../views/admin/quotes';
import RenewalsView from '../views/shared/policies/auto/Renewals';
import VehicleVerification from '../views/shared/vehicle-verification';
import Loans from '../views/partners/financier/loans';
import RnPl from '../views/shared/rnpl';
import Financing from '../views/shared/premiumFinancing';
import ClaimsView from '../views/shared/claims/index';
import PartnerFuelSales from '../views/partners/fuel/PartnerFuelSales';
import FuelVendorSalesHistory from '../views/partners/fuel/FuelVendorSalesHistory';
import Transactions from '../views/partners/financier/transactions';
import TeamView from '../views/partners';
import InsurersView from '../views/partners/insurers';
import BlogsView from '../views/admin/blogs';
import InsurerResourcesView from '../views/shared/resources';
import SetupInsurerStore from '../components/dashboard/insurers/store/InsurerStoreSetup';
import PartnerFuelQuota from '../views/partners/fuel/FuelQuota';
import FuelSalesAuditview from '../views/partners/fuel/FuelSalesAuditview';
import InsurerReviewsView from '../views/partners/insurers/insurerDetails/Reviews';
import PartnerCommissionsView from '../views/partners/broker/commissions/CommissionsPage';
import PayoutsView from '../views/partners/shared/payouts/index';
import SettlementsView from '../views/partners/shared/settlements';
import Disbursements from '../views/partners/financier/disbursements/Disbursements';
import PaymentsView from '../views/shared/payments';
import Bills from '../views/partners/shared/bills';
import AuditLogs from '../views/admin/auditLogs';
import SettingsView from '../views/shared/settings';

const Approvals = lazy(() => import('../views/admin/approvals'));
const InsurerSales = lazy(() => import('../views/shared/payments/InsurerSales'));
const MessagesView = lazy(() => import('../views/admin/messages'));
const NotificationsView = lazy(() => import('../views/admin/notifications'));
const BrokerActivations = lazy(() => import('../views/admin/brokerActivations'));
const InsurerDetails = lazy(() => import('../views/partners/insurers/insurerDetails'));
const InsurerEdit = lazy(() => import('../views/partners/insurers/EditInsurer'));
const CreateInspectionFleet = lazy(() => import('../views/shared/vehicle-verification/CreateInspectionFleet'));
const ImportPoliciesView = lazy(() => import('../views/shared/policies/auto/import-policies'));
const PolicyDetailsView = lazy(() => import('../views/shared/policies/policyDetailsView'));
const FleetDetailsView = lazy(() => import('../views/shared/policies/auto/policyDetails/fleetDetails/index'))
const DynamicCreatePolicyView = lazy(() => import('../views/shared/policies/components/DynamicCreatePolicyView'));
const DynamicEditPolicyView = lazy(() => import('../views/shared/policies/components/DynamicEditPolicyView'));
const CreatePolicyView = lazy(() => import('../views/shared/policies/auto/AdminCreatePolicy'));
const IssuedMotorPolicyCreation = lazy(() => import('../views/shared/policies/auto/issuedMotor/import-issued-policies/index'))
const FuelView = lazy(() => import('../views/admin/fuel'));
const QuotesDetails = lazy(() => import('../views/admin/quotes/ViewQuote'));
const QuoteCalculatorView = lazy(() => import('../views/admin/quotes/calculators/paginatedChecker'));
const TicketsView = lazy(() => import('../views/admin/tickets'));
const Financials = lazy(() => import('../views/admin/financials'));
const AgentTeam = lazy(() => import('../views/partners/agency/team'));
const PayoutDetails = lazy(() => import('../views/partners/shared/payouts/components/PayoutDetails'));
const CommunitiesView = lazy(() => import('../views/admin/communities'));
const SingleCommunityView = lazy(() => import('../views/admin/communities/ViewCommunity'));
const EditClaim = lazy(() => import('../views/shared/claims/EditClaim'));
const ClaimsDetailsView = lazy(() => import('../views/shared/claims/ClaimDetailsView'));
const TasksView = lazy(() => import('../views/admin/Tasks'));
const CreateBlogView = lazy(() => import('../views/admin/blogs/CreateBlog'));
const ExpenseDetailsView = lazy(() => import('../views/shared/settings/expenses/ExpenseDetailsView'));
const Documentation = lazy(() => import('../views/admin/documentation'));
const CreatePremiumFinancingApplication = lazy(() => import('../views/shared/premiumFinancing/applicationCreation/index'));
const InsurerProvideMIDAccess = lazy(() => import('../views/shared/settings/Intermediaries/components/ApproveMIDAccess'));


// Partners
const LoanDetails = lazy(() => import('../views/partners/financier/loans/LoanDetails'));
const RepaymentsView = lazy(() => import('../views/partners/financier/repayments/index'));
const RepaymentDetailPage = lazy(() => import('../views/partners/financier/repayments/RepaymentDetailsPage'));
const SettlementDetails = lazy(() => import('../views/partners/shared/settlements/SettlementDetails'));
const DisbursementDetailPage = lazy(() => import('../views/partners/financier/disbursements/DisbursementDetailPage'));
const AddRiskType = lazy(() => import('../views/shared/settings/insuranceConfiguration/AddRiskTypePage'));
const AddInsuranceType = lazy(() => import('../views/shared/settings/insuranceConfiguration/AddInsuranceTypePage'))
const AddProductConfiguration = lazy(() => import('../views/shared/settings/insuranceConfiguration/AddProductModal'))
const BrokerUploadPage = lazy(() => import('../views/shared/brokerMappingTool/BrokerUploadPage'));
const InternalUploadsListPage = lazy(() => import('../views/shared/brokerMappingTool/InternalUploadsListPage'));
 

function Router() {
  const { user } = useUserAccount();
  const { getDashboardBase } = useRoute();
  const { isInsurerOnboarding } = useAuth();
  const { getPreviousPath } = useNavigationStack();
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useDispatch()

  const fetchAllEnums = async () => {
    try {
      const response: any = await getAllEnums();
      if (response) {
        const enums: IEnumsResponse = response;
        dispatch(UPDATE_ENUMS({
          claimStages: [...enums.claimStages],
          protectionTypes: [...enums.protectionTypes],
          leadSources: [...enums.leadSources],
          vehicleUsages: [...enums.vehicleUsages],
          idTypes: [...enums.idTypes],
          usageGroups: [...enums.usageGroups],
          companyTypes: [...enums.companyTypes],
          accountTypes: [...enums.accountTypes],
          accountProviders: [...enums.accountProviders],
          genders: [...enums.genders],
          employmentTypes: [...enums.employmentTypes],
          policyTypes: [...enums.policyTypes],
          installmentPeriods: [...enums.installmentPeriods],
          paymentStatuses: [...enums.paymentStatuses],
          reviewStages: [...enums.reviewStages],
          fuelSaleStatuses: [...enums.fuelSaleStatuses],
          currencyCodes: [...enums.currencyCodes],
          bodyTypes: [...enums.bodyTypes],
          vehicleBodyTypes: [...enums.vehicleBodyTypes],
          planTiers: [...enums.planTiers],
          billingCycles: [...enums.billingCycles],
          portfolios: [...enums.portfolios],
          claimTypes: [...enums.claimTypes],
          issuanceModes: [...enums.issuanceModes],
        }))
      }
    } catch (error) {
      console.log(error);
    }
  }

  useQuery({
    queryKey: ['fetchAllEnums', dispatch],
    queryFn: fetchAllEnums,
  })

  useEffect(() => {
    if (isInsurerOnboarding()) {
      navigate(`${getDashboardBase()}/onboarding`);
    }
  }, [user]);

  useEffect(() => {
    if (isProduction()) {
      ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS || "");
      ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
    }
  }, []);

  useEffect(() => {
    if (isProduction()) {
      ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
    }
  }, [location]);

  const dashboardLayout = useMemo(() => {
    return user.isFuelProvider()
      ? <DashboardLayout showSideBar={false} />
      : <DashboardLayout showSideBar />;
  }, [user]);

  return (
    <div className="font-body">
      <Routes>
        {/* -------------------- AUTH ROUTES ----------------------------------- */}


        <Route
          path="/public/intermediary/approve-mid-access"
          element={
            <PublicRouteWrapper>
              <InsurerProvideMIDAccess />
            </PublicRouteWrapper>
          }
          caseSensitive
        />

        <Route path="/" element={<AuthLayout />} caseSensitive>
          <Route index element={<Navigate replace to={SIGN_IN} />} />
          <Route path={SIGN_IN} element={<Auth />} />
          <Route path={FORGOT_PASSWORD} element={<ForgotPassword />} />
          <Route path={RESET_PASSWORD} element={<ResetPassword />} />

          <Route path="*" element={<Navigate replace to={SIGN_IN} />} />
        </Route>


        <Route path={SIGN_IN} element={<Auth />} index caseSensitive />
        <Route path={COMPLETE_ACCOUNT} element={<CompleteAccount />} caseSensitive />
        {/* -------------------- DASHBOARD ROUTES ----------------------------------- */}

        <Route path={DASHBOARD_BASE} element={dashboardLayout} caseSensitive>
          <Route
            path={`${getDashboardBase()}/dashboard`}
            element={
              <RouteWrapper>
                <DashboardView />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/blogs`}
            element={
              <RouteWrapper>
                <BlogsView />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/blogs/create-blog`}
            element={
              <RouteWrapper>
                <CreateBlogView />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/blogs/:blogId/edit`}
            element={
              <RouteWrapper>
                <CreateBlogView />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/reviews`}
            element={
              <RouteWrapper>
                <InsurerReviewsView />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/communities`}
            element={
              <RouteWrapper>
                <CommunitiesView />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/communities/:communityId`}
            element={
              <RouteWrapper>
                <SingleCommunityView />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/leads`}
            element={
              <RouteWrapper>
                <LeadsView />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/policies/create-policy`}
            element={
              <RouteWrapper>
                <CreatePolicyView />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/policies/dynamic-create-policy`}
            element={
              <RouteWrapper>
                <DynamicCreatePolicyView />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/policies`}
            element={
              <RouteWrapper>
                <PoliciesView />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/policies/:policyId`}
            element={
              <RouteWrapper>
                <PolicyDetailsView />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/policies/import-policies`}
            element={
              <RouteWrapper>
                <ImportPoliciesView />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/policies/fleets/:fleetId`}
            element={
              <RouteWrapper>
                <FleetDetailsView />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/quotes`}
            element={
              <RouteWrapper>
                <QuotesView />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/quotes/:ref`}
            element={
              <RouteWrapper>
                <QuotesDetails />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/quotes/calculate`}
            element={
              <RouteWrapper>
                <QuoteCalculatorView />
              </RouteWrapper>
            }
            caseSensitive
            index
          />
          <Route
            path={`${getDashboardBase()}/quotes/:ref`}
            element={
              <RouteWrapper>
                <QuotesDetails />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/fuel`}
            element={
              <RouteWrapper>
                <FuelView />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/payments`}
            element={
              <RouteWrapper>
                <PaymentsView />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/bills`}
            element={
              <RouteWrapper>
                <Bills />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/bills/:billId`}
            element={
              <RouteWrapper>
                <BillDetailsView />
              </RouteWrapper>
            }
            caseSensitive
          />

          {user.isOwner() && (
            <>
              <Route
                path={`${getDashboardBase()}/approvals`}
                element={
                  <RouteWrapper>
                    <Approvals />
                  </RouteWrapper>
                }
                caseSensitive
                index
              />

              <Route
                path={`${getDashboardBase()}/audit`}
                element={
                  <RouteWrapper>
                    <AuditLogs />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/quota/:partnerId`}
                element={
                  <RouteWrapper>
                    <PartnerFuelQuota />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/broker-activations`}
                element={
                  <RouteWrapper>
                    <BrokerActivations />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />
            </>
          )}

          {(user.isSupervisor() || user.isOwner()) && (
            <>
              <Route
                path={`${getDashboardBase()}/tickets`}
                element={
                  <RouteWrapper>
                    <TicketsView />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/customers`}
                element={
                  <RouteWrapper>
                    <CustomersView />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/vehicles`}
                element={
                  <RouteWrapper>
                    <Vehicles />
                  </RouteWrapper>
                }
                caseSensitive
                index
              />

              <Route
                path={`${getDashboardBase()}/messages`}
                element={
                  <RouteWrapper>
                    <MessagesView />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/notifications`}
                element={
                  <RouteWrapper>
                    <NotificationsView />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/teams`}
                element={
                  <RouteWrapper>
                    <TeamView />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/teams/:id`}
                element={
                  <RouteWrapper>
                    <CompanyDetail />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/teams/staff/:managerId`}
                element={
                  <RouteWrapper>
                    <RelationshipManagerTeam />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/insurers`}
                element={
                  <RouteWrapper>
                    <InsurersView />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/insurers/:id/edit`}
                element={
                  <RouteWrapper>
                    <InsurerEdit />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/insurers/add-insurer`}
                element={
                  <RouteWrapper>
                    <AddInsurer />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={DASHBOARD_BASE}
                element={<Navigate to={DASHBOARD} replace />}
                caseSensitive
              />
            </>
          )}

          {user.isAdministrator() && (
            <>
              <Route
                path={`${getDashboardBase()}/teams/:id`}
                element={
                  <RouteWrapper>
                    <CompanyDetail />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />


              <Route
                path={`${getDashboardBase()}/customers`}
                element={
                  <RouteWrapper>
                    <CustomersView />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />
            </>
          )}

          {(user.isAdministrator() || user.isRideSharing() || user.isInsurer()) && (
            <>
              <Route
                path={`${getDashboardBase()}/onboarding`}
                element={
                  <RouteWrapper>
                    <InsurerOnboarding />
                  </RouteWrapper>
                }
                caseSensitive
                index
              />

              <Route
                path={`${getDashboardBase()}/store`}
                element={
                  <RouteWrapper>
                    <SetupInsurerStore />
                  </RouteWrapper>
                }
                caseSensitive
                index
              />

              <Route
                path={`${getDashboardBase()}/store/preview`}
                element={
                  <RouteWrapper>
                    <InsurerStorePreview />
                  </RouteWrapper>
                }
                caseSensitive
                index
              />

              <Route
                path={`${getDashboardBase()}/resources`}
                element={
                  <RouteWrapper>
                    <InsurerResourcesView />
                  </RouteWrapper>
                }
                caseSensitive
                index
              />
            </>
          )}

          {(user.isAdministrator() || user.isManager()) && (
            <>
              <Route
                path={`${getDashboardBase()}/fuel-audit`}
                element={
                  <RouteWrapper>
                    <FuelSalesAuditview />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/sales`}
                element={
                  <RouteWrapper>
                    <PartnerFuelSales />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />
            </>
          )}

          {user.isManager() && (
            // {user.isFuelManager() && (
            <Route
              path={`${getDashboardBase()}/quota`}
              element={
                <RouteWrapper>
                  <PartnerFuelQuota />
                </RouteWrapper>
              }
              index
              caseSensitive
            />
          )}

          {user.isAgent() && (
            // {user.isFuelVendor() && (
            <Route
              path={`${getDashboardBase()}/sales`}
              element={
                <RouteWrapper>
                  <FuelVendorSalesHistory />
                </RouteWrapper>
              }
              index
              caseSensitive
            />
          )}

          {user.isInsurer() && (
            <>
              <Route
                path={`${getDashboardBase()}/sales`}
                element={
                  <RouteWrapper>
                    <InsurerSales />
                  </RouteWrapper>
                }
                caseSensitive
                index
              />
            </>
          )}

          {(user.isBroker() || user.isOwner()) && (
            <>
              <Route
                path={`${getDashboardBase()}/customers`}
                element={
                  <RouteWrapper>
                    <CustomersView />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/commissions`}
                element={
                  <RouteWrapper>
                    <PartnerCommissionsView />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />
            </>
          )}

          {user?.isInternalAdmin() &&
            <Route
              path={`${getDashboardBase()}/audit`}
              element={
                <RouteWrapper>
                  <AuditLogs />
                </RouteWrapper>
              }
              index
              caseSensitive
            />
          }

          <Route
            path={`${getDashboardBase()}/settlements`}
            element={
              <RouteWrapper>
                <SettlementsView />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={user.isBroker() ? `${getDashboardBase()}/commissions/settlements/:settlementId` : `${getDashboardBase()}/settlements/:settlementId`}
            element={
              <RouteWrapper>
                <SettlementDetails previousPath={getPreviousPath()} />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          {(user.isFinancier() || user.isInternalAdmin()) && (
            <>
              <Route
                path={`${getDashboardBase()}/loans`}
                element={
                  <RouteWrapper>
                    <Loans />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/loans/:loanId`}
                element={
                  <RouteWrapper>
                    <LoanDetails />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/repayments`}
                element={
                  <RouteWrapper>
                    <RepaymentsView />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/repayments/:repaymentId`}
                element={
                  <RouteWrapper>
                    <RepaymentDetailPage />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />


              <Route
                path={`${getDashboardBase()}/transactions`}
                element={
                  <RouteWrapper>
                    <Transactions />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/transactions/:transactionId`}
                element={
                  <RouteWrapper>
                    <TransactionDetailPage />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/bills`}
                element={
                  <RouteWrapper>
                    <Bills />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/disbursements`}
                element={
                  <RouteWrapper>
                    <Disbursements />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />

              <Route
                path={`${getDashboardBase()}/disbursements/:disbursementId`}
                element={
                  <RouteWrapper>
                    <DisbursementDetailPage />
                  </RouteWrapper>
                }
                index
                caseSensitive
              />
            </>
          )}

          <Route
            path={`${getDashboardBase()}/payouts`}
            element={

              <RouteWrapper>
                <PayoutsView showTitleSection={true} />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/customers/:id`}
            element={
              <RouteWrapper>
                <ViewCustomer previousPath={getPreviousPath()} />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/financials`}
            element={
              <RouteWrapper>
                <Financials />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/payouts/:payoutId`}
            element={
              <RouteWrapper>
                <PayoutDetails previousPath={getPreviousPath()} />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/teams/:teamId/payouts/:payoutId`}
            element={
              <RouteWrapper>
                <PayoutDetails previousPath={getPreviousPath()} />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/insurers/:teamId/payouts/:payoutId`}
            element={
              <RouteWrapper>
                <PayoutDetails previousPath={getPreviousPath()} />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/insurers/:id`}
            element={
              <RouteWrapper>
                <InsurerDetails />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/team`}
            element={
              <RouteWrapper>
                <AgentTeam />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/policies/:policyId/edit`}
            element={
              <RouteWrapper>
                <DynamicEditPolicyView />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/policies/auto-policy/add-issued-motor-policy`}
            element={
              <RouteWrapper>
                <IssuedMotorPolicyCreation />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/tasks`}
            element={
              <RouteWrapper>
                <TasksView />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route path={`${getDashboardBase()}/settings`}>
            <Route index element={
              <RouteWrapper>
                <SettingsView />
              </RouteWrapper>} />

            <Route
              path='roles/role/:id'
              element={<RoleDetailsView />}
              caseSensitive
            />

            <Route
              path='roles/create-role'
              element={<CreateRoleView />}
              caseSensitive
            />

            <Route path="billing/subscription-plan" element={<CreateSubscriptionPlan />} />
          </Route>

          <Route
            path={`${getDashboardBase()}/renewals`}
            element={
              <RouteWrapper>
                <RenewalsView />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/claims`}
            element={
              <RouteWrapper>
                <ClaimsView />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/claims/:claimNumber`}
            element={
              <RouteWrapper>
                <ClaimsDetailsView />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/claims/:claimNumber/edit`}
            element={
              <RouteWrapper>
                <EditClaim />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/leads/create-leads`}
            element={
              <RouteWrapper>
                <CreateLeads />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/leads/:id`}
            element={
              <RouteWrapper>
                <LeadDetailsView />
              </RouteWrapper>
            }
            index
            caseSensitive
          />


          <Route
            path={`${getDashboardBase()}/rnpl`}
            element={
              <RouteWrapper>
                <RnPl />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/rnpl/:id`}
            element={
              <RouteWrapper>
                <RNPLApplicantDetails previousPath={getPreviousPath()} />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/premium-financing`}
            element={
              <RouteWrapper>
                <Financing />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/premium-financing/add-premium`}
            element={
              <RouteWrapper>
                <AdminCreatePremiumFinancingApplication />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/premium-financing/application-creation`}
            element={
              <RouteWrapper>
                <CreatePremiumFinancingApplication />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/premium-financing/:id`}
            element={
              <RouteWrapper>
                <PremiumFinancingApplicationDetails previousPath={getPreviousPath()} />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/premium-financing/:id/edit`}
            element={
              <RouteWrapper>
                <PremiumFinancingEdit />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/expenses/:id`}
            element={
              <RouteWrapper>
                <ExpenseDetailsView />
              </RouteWrapper>
            }
            index
            caseSensitive
          />

          <Route
            path={`${getDashboardBase()}/vehicle-verification`}
            element={
              <RouteWrapper>
                <VehicleVerification />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/vehicle-verification/:vehicleVerificationId`}
            element={
              <RouteWrapper>
                <VerificationDetails />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/vehicle-verification/create-fleet`}
            element={
              <RouteWrapper>
                <CreateInspectionFleet />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          <Route
            path={`${getDashboardBase()}/documentation`}
            element={
              <RouteWrapper>
                <Documentation />
              </RouteWrapper>
            }
            caseSensitive
            index
          />

          {user.isInternalAdmin() && (
            <>
              <Route
                path={`${getDashboardBase()}/insurance-configuration/insurance-risk-type`}
                element={
                  <RouteWrapper>
                    <AddRiskType />
                  </RouteWrapper>
                }
                caseSensitive
                index
              />

              <Route
                path={`${getDashboardBase()}/insurance-configuration/insurance-type`}
                element={
                  <RouteWrapper>
                    <AddInsuranceType />
                  </RouteWrapper>
                }
                caseSensitive
                index
              />

              <Route
                path={`${getDashboardBase()}/insurance-configuration/create-product`}
                element={
                  <RouteWrapper>
                    <AddProductConfiguration />
                  </RouteWrapper>
                }
                caseSensitive
                index
              />
            </>

          )
          }

          {/* -------------------- BROKER MAPPING TOOL ----------------------------------- */}

          {/* Broker-only: upload raw spreadsheets. */}
          {user.isBroker() && (
            <Route
              path={`${getDashboardBase()}/mapping-tool/upload`}
              element={
                <RouteWrapper>
                  <BrokerUploadPage />
                </RouteWrapper>
              }
              caseSensitive
              index
            />
          )}

          {/* Internal-only: list every broker upload, launch external mapping
              portal, import the mapped CSV back. */}
          {user.isInternalAdmin() && (
            <Route
              path={`${getDashboardBase()}/mapping-tool/broker-uploads`}
              element={
                <RouteWrapper>
                  <InternalUploadsListPage />
                </RouteWrapper>
              }
              caseSensitive
              index
            />
          )}
        </Route>

        {/* -------------------- ERROR ROUTES ----------------------------------- */}
        <Route element={<ErrorsLayout />} caseSensitive>
          <Route path={NOT_FOUND} element={<NotFound />} caseSensitive />
        </Route>
      </Routes>
    </div>
  );
}

export default Router;
